import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getDocs as getAllDocs } from 'firebase/firestore';
import './Subscriptions.css';
import '../components/SubscriptionFilters.css';
import { exportSubscriptionsToExcel } from '../utils/exportToExcel';

const Subscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [subscriptionsPerPage] = useState(10);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const parseTimestamp = (value) => {
    if (!value) return null;

    // Already a Date object
    if (value instanceof Date) {
      return value;
    }

    // Firestore Timestamp instance
    if (typeof value.toDate === 'function') {
      return value.toDate();
    }

    // Firestore timestamp object ({ seconds, nanoseconds })
    if (typeof value === 'object' && value !== null && 'seconds' in value) {
      return new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1_000_000);
    }

    // Numeric epoch values
    if (typeof value === 'number') {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    // ISO or date strings
    if (typeof value === 'string') {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    return null;
  };

  const getFormattedDate = (value, fallback = 'Not provided') => {
    const date = parseTimestamp(value);
    return date ? date.toLocaleDateString() : fallback;
  };

  const getUserProfile = async (phoneNumber, cache) => {
    if (!phoneNumber) return null;

    if (cache.has(phoneNumber)) {
      return cache.get(phoneNumber);
    }

    let userData = null;
    try {
      const userDoc = await getDoc(doc(db, 'users', phoneNumber));
      if (userDoc.exists()) {
        userData = userDoc.data();
      }
    } catch (error) {

    }

    cache.set(phoneNumber, userData);
    return userData;
  };

  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);

      const usersSnapshot = await getAllDocs(collection(db, 'users'));
      const userDocs = usersSnapshot.docs;

      // Prepare all plan fetch promises in parallel
      const planPromises = userDocs.map(async (userDoc) => {
        const userData = userDoc.data();
        const phoneNumber = userData.mobileNumber || userDoc.id;
        const planDocRef = doc(db, 'users', phoneNumber, 'subscription', 'plan');
        const planDoc = await getDoc(planDocRef);
        if (planDoc.exists()) {
          const subscriptionData = planDoc.data();
          const startDate = parseTimestamp(subscriptionData.startDate || subscriptionData.validFrom || subscriptionData.createdAt);
          const validityDate = parseTimestamp(subscriptionData.validity || subscriptionData.validUntil || subscriptionData.validityDate || subscriptionData.validTill || subscriptionData.expiryDate);
          const priceValue = parseFloat(subscriptionData.amount ?? subscriptionData.price);
          const price = Number.isFinite(priceValue) ? priceValue : 0;
          return {
            id: phoneNumber,
            phoneNumber,
            userName: userData?.name || 'Unknown User',
            userAddress: userData?.address || 'Not provided',
            planName: subscriptionData.planName || 'Not specified',
            price,
            formattedStartDate: startDate ? startDate.toLocaleDateString() : 'Not provided',
            formattedValidityDate: validityDate ? validityDate.toLocaleDateString() : 'Not provided',
            startDate,
            validity: validityDate,
            status: subscriptionData.status || 'Success',
            paymentMethod: subscriptionData.paymentMethod || 'Unknown',
            isActive: validityDate ? new Date() <= validityDate : false
          };
        }
        return null;
      });

      // Wait for all plan fetches to complete
      const subscriptionsList = (await Promise.all(planPromises)).filter(Boolean);

      // Sort by start date (newest first)
      subscriptionsList.sort((a, b) => {
        const aTime = a.startDate ? a.startDate.getTime() : 0;
        const bTime = b.startDate ? b.startDate.getTime() : 0;
        return bTime - aTime;
      });

      setSubscriptions(subscriptionsList);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get unique plan names for dropdown
  const planOptions = Array.from(new Set(subscriptions.map(s => s.planName))).filter(Boolean);

  // Filter subscriptions based on search term and plan
  const filteredSubscriptions = subscriptions.filter(subscription => {
    const matchesSearch =
      subscription.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscription.phoneNumber.includes(searchTerm) ||
      subscription.planName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlan = planFilter ? subscription.planName === planFilter : true;
    return matchesSearch && matchesPlan;
  });

  // Pagination
  const indexOfLastSubscription = currentPage * subscriptionsPerPage;
  const indexOfFirstSubscription = indexOfLastSubscription - subscriptionsPerPage;
  const currentSubscriptions = filteredSubscriptions.slice(indexOfFirstSubscription, indexOfLastSubscription);
  const totalPages = Math.ceil(filteredSubscriptions.length / subscriptionsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getPlanBadge = (planName) => {
    const planColors = {
      'yearly': 'plan-yearly',
      'monthly': 'plan-monthly',
      'weekly': 'plan-weekly'
    };

    return (
      <span className={`plan-badge ${planColors[planName?.toLowerCase()] || 'plan-default'}`}>
        {planName?.toUpperCase() || 'UNKNOWN'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="subscriptions-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading subscriptions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="subscriptions-page">
      <div className="page-header subscriptions-header" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 style={{ marginRight: 24 }}>Subscriptions</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search subscriptions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
              style={{ minWidth: 220 }}
            />
          </div>
          <div className="stats-container" style={{ display: 'flex', gap: 8 }}>
            <div className="stat-item">
              <span className="stat-number">{subscriptions.length}</span>
              <span className="stat-label">Total</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{subscriptions.filter(s => s.isActive).length}</span>
              <span className="stat-label">Active</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filter row above the table, left aligned */}
      <div className="subscription-filters-row" style={{ marginLeft: 0, marginTop: 0 }}>
        <label className="subscription-filter-label-inline">Plan:
          <select
            className="subscription-filter-select"
            value={planFilter}
            onChange={e => setPlanFilter(e.target.value)}
          >
            <option value="">All</option>
            {planOptions.map(plan => (
              <option key={plan} value={plan}>{plan}</option>
            ))}
          </select>
        </label>
        <button
          className="subscription-export-btn"
          onClick={() => exportSubscriptionsToExcel(filteredSubscriptions, 'subscriptions.xlsx')}
        >
          Export
        </button>
      </div>

      <div className="subscriptions-content">
        {currentSubscriptions.length > 0 ? (
          <>
            <div className="subscriptions-table">
              <table>
                <thead>
                  <tr>
                    <th>Sr. No</th>
                    <th>Name</th>
                    <th>Phone Number</th>
                    <th>Plan</th>
                    <th>Price</th>
                    <th>Start Date</th>
                    <th>Validity Date</th>
                  </tr>
                </thead>
                <tbody>
                  {currentSubscriptions.map((subscription, index) => (
                    <tr key={subscription.id}>
                      <td>{indexOfFirstSubscription + index + 1}</td>
                      <td>{subscription.userName}</td>
                      <td>
                        <span className="phone-number">{subscription.phoneNumber}</span>
                      </td>
                      <td>{getPlanBadge(subscription.planName)}</td>
                      <td>
                        <span className="price">₹{subscription.price}</span>
                      </td>
                      <td>
                        <span className="date">{subscription.formattedStartDate}</span>
                      </td>
                      <td>
                        <span className="date">{subscription.formattedValidityDate}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>

                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => paginate(index + 1)}
                    className={`pagination-btn ${currentPage === index + 1 ? 'active' : ''}`}
                  >
                    {index + 1}
                  </button>
                ))}

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="no-data">
            <div className="no-data-icon">💳</div>
            <h3>No Subscriptions Found</h3>
            <p>No subscription data available at the moment.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Subscriptions;
